import * as Dialog from '@radix-ui/react-dialog';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronLeft, ChevronRight, Download, Loader2, Maximize, Minus, Plus, RotateCw, X } from 'lucide-react';
import { detectDelimiter, delimitedKind, filePreviewKind, parseCsv, type FilePreviewKind } from '@kaba/shared';
import { cx, IconButton } from '../ui/ui';
import { FileIcon } from './FileIcon';

/** Was die Vorschau von einer Datei braucht (Kartenanhang oder später Kommentar-Anhang) */
export interface PreviewItem {
  id: string;
  filename: string;
  mime: string;
  size: number;
  isImage: boolean;
  url: string;
  thumbUrl: string | null;
  /** Verkleinerte Großansicht (bis 2048 px); Original nur bei 1:1 bzw. starkem Zoom */
  previewUrl?: string | null;
  previewWidth?: number | null;
  width?: number | null;
  height?: number | null;
  /** Abweichende Download-Adresse (z. B. PDF-Bericht); sonst die des Anhangs */
  downloadUrl?: string;
  /** Name, aus dem die Dateiart bestimmt wird, wenn `filename` ergänzt ist (z. B. „… (Fassung 2)“) */
  typeName?: string;
}

/**
 * Vorschau nur für tatsächlich unterstützte Typen (`filePreviewKind` in shared): Bilder erst, wenn der Server sie
 * verarbeiten konnte (`isImage`), Office-Dokumente als vom Server erzeugtes PDF, Audio/Video mit den Bordmitteln
 * des Browsers, Text nur als Text. HTML und SVG werden nie eingebettet.
 */
export function previewKind(it: Pick<PreviewItem, 'isImage' | 'mime' | 'filename' | 'typeName'>): FilePreviewKind {
  return filePreviewKind({ isImage: it.isImage, mime: it.mime, filename: it.typeName ?? it.filename });
}

/** PDF-Fassung eines Office-Dokuments: gleiche Adresse, Variante `pdf` statt `orig` */
const officePdfUrl = (it: PreviewItem) => it.url.replace(/\/orig(\?.*)?$/, '/pdf');

const TEXT_LIMIT = 512 * 1024;

function formatSize(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

const downloadUrl = (it: PreviewItem) => it.downloadUrl ?? `/api/files/${it.id}/download`;

/**
 * Vorschau-Fenster: am Desktop groß über der Karte, am Handy als Vollbild. Escape schließt nur die
 * Vorschau; beim Schließen kehrt der Fokus zur auslösenden Stelle zurück (Radix).
 */
export function AttachmentPreview({
  items,
  index,
  onIndex,
  onClose,
}: {
  items: PreviewItem[];
  index: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const open = index !== null && !!items[index];
  // Auslöser merken, bevor Radix den Fokus ins Fenster setzt – beim Schließen dorthin zurück
  const trigger = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open) trigger.current = document.activeElement as HTMLElement | null;
  }, [open]);
  const it = open ? items[index] : null;
  const many = items.length > 1;
  const go = useCallback(
    (d: number) => {
      if (index === null || !many) return;
      onIndex((index + d + items.length) % items.length);
    },
    [index, many, items.length, onIndex],
  );

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[55] bg-black/70" />
        <Dialog.Content
          className="fixed inset-0 z-[56] flex flex-col bg-[var(--panel)] outline-none sm:inset-[3vh_3vw] sm:overflow-hidden sm:rounded-2xl sm:shadow-2xl"
          onKeyDown={(e) => {
            const t = e.target as HTMLElement;
            if (t.closest('input, textarea, video, audio')) return;
            if (e.key === 'ArrowLeft') go(-1);
            else if (e.key === 'ArrowRight') go(1);
            else return;
            e.preventDefault();
          }}
          aria-describedby={undefined}
          onCloseAutoFocus={(e) => {
            const t = trigger.current;
            if (t?.isConnected) {
              e.preventDefault();
              t.focus({ preventScroll: true });
            }
          }}
        >
          {it && (
            <>
              <div className="pt-safe flex items-center gap-1 border-b border-[var(--line)] py-1.5 pr-2 pl-4">
                <div className="min-w-0 flex-1">
                  <Dialog.Title className="truncate text-sm font-semibold">{it.filename}</Dialog.Title>
                  <p className="text-xs text-[var(--ink-soft)]">
                    {formatSize(it.size)}
                    {many && ` · Datei ${index! + 1} von ${items.length}`}
                  </p>
                </div>
                {many && (
                  <>
                    <IconButton label="Vorherige Datei (←)" onClick={() => go(-1)} className="size-11 sm:size-10">
                      <ChevronLeft size={20} />
                    </IconButton>
                    <IconButton label="Nächste Datei (→)" onClick={() => go(1)} className="size-11 sm:size-10">
                      <ChevronRight size={20} />
                    </IconButton>
                  </>
                )}
                <a
                  href={downloadUrl(it)}
                  className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 hover:text-[var(--ink)] sm:size-10 dark:hover:bg-white/10"
                  aria-label={`${it.filename} herunterladen`}
                  title="Herunterladen"
                >
                  <Download size={19} />
                </a>
                <Dialog.Close asChild>
                  <IconButton label="Vorschau schließen (Esc)" className="size-11 sm:size-10">
                    <X size={20} />
                  </IconButton>
                </Dialog.Close>
              </div>
              <div className="relative min-h-0 flex-1 bg-[var(--surface)]">
                <PreviewBody key={it.id} it={it} />
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PreviewBody({ it }: { it: PreviewItem }) {
  const kind = previewKind(it);
  if (kind === 'image') return <ImageView it={it} />;
  if (kind === 'pdf') return <PdfView it={it} />;
  if (kind === 'office') return <OfficeView it={it} />;
  if (kind === 'video' || kind === 'audio') return <MediaPlayer it={it} kind={kind} />;
  if (kind === 'text') return <TextView it={it} />;
  return <NoPreview it={it} text="Für diesen Dateityp ist keine Vorschau verfügbar." />;
}

function NoPreview({ it, text, onRetry }: { it: PreviewItem; text: string; onRetry?: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center" role="status">
      <span className="flex size-16 items-center justify-center rounded-2xl bg-black/5 dark:bg-white/10">
        <FileIcon mime={it.mime} filename={it.typeName ?? it.filename} size={30} />
      </span>
      <p className="max-w-full truncate font-medium">{it.filename}</p>
      <p className="text-sm text-[var(--ink-soft)]">
        {it.mime || 'Unbekannter Typ'} · {formatSize(it.size)}
      </p>
      <p className="text-sm">{text}</p>
      <div className="flex flex-wrap justify-center gap-2">
        {onRetry && (
          <button type="button" onClick={onRetry} className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-black/5 px-3.5 text-sm font-medium hover:bg-black/10 sm:min-h-10 dark:bg-white/10">
            <RotateCw size={16} aria-hidden /> Erneut versuchen
          </button>
        )}
        <a href={downloadUrl(it)} className="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-accent px-3.5 text-sm font-medium text-on-accent hover:brightness-110 sm:min-h-10">
          <Download size={16} aria-hidden /> Herunterladen
        </a>
      </div>
    </div>
  );
}

function Toolbar({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-1/2 z-10 flex -translate-x-1/2 items-center gap-0.5 rounded-xl border border-[var(--line)] bg-[var(--panel)]/95 p-1 shadow-lg" role="toolbar" aria-label="Ansicht">
      {children}
    </div>
  );
}

function ToolBtn({ label, onClick, children, disabled }: { label: string; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg px-2 text-sm font-medium hover:bg-black/5 disabled:opacity-40 sm:min-h-9 sm:min-w-9 dark:hover:bg-white/10"
    >
      {children}
    </button>
  );
}

/* ---------- Bilder ---------- */

function ImageView({ it }: { it: PreviewItem }) {
  // „fit“ = ins Fenster einpassen; sonst Faktor zur Originalgröße
  const [zoom, setZoom] = useState<'fit' | number>('fit');
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  // null = noch nicht gemessen (sonst würde kurz 1:1 angenommen und das Original geladen)
  const [fitScale, setFitScale] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  // Erst die verkleinerte Großansicht laden; das Original nur, wenn stärker vergrößert wird
  const [full, setFull] = useState(false);
  // SVG nie als Original einbetten – die vom Server erzeugte Rastergrafik genügt
  const src = it.mime === 'image/svg+xml' ? (it.thumbUrl ?? it.url) : !full && it.previewUrl ? it.previewUrl : it.url;

  const measureFit = useCallback(() => {
    if (!natural || !box.current) return;
    const r = box.current.getBoundingClientRect();
    setFitScale(Math.min(1, (r.width - 32) / natural.w, (r.height - 32) / natural.h));
  }, [natural]);
  useEffect(() => {
    measureFit();
    window.addEventListener('resize', measureFit);
    return () => window.removeEventListener('resize', measureFit);
  }, [measureFit]);

  const scale = zoom === 'fit' ? (fitScale ?? 1) : zoom;
  const measured = zoom !== 'fit' || fitScale !== null;
  // Großansicht reicht nicht mehr: Original nachladen
  useEffect(() => {
    if (!full && measured && it.previewUrl && natural && it.previewWidth && natural.w * scale * Math.min(window.devicePixelRatio || 1, 1.5) > it.previewWidth * 1.05) setFull(true);
  }, [full, measured, it.previewUrl, it.previewWidth, natural, scale]);
  const step = (f: number) => setZoom(Math.min(8, Math.max(0.1, Math.round(scale * f * 100) / 100)));

  // Verschieben bei Zoom: mit Maus/Stift ziehen (Touch scrollt ohnehin)
  const drag = useRef<{ x: number; y: number; l: number; t: number } | null>(null);
  const onDown = (e: ReactPointerEvent) => {
    if (zoom === 'fit' || e.pointerType === 'touch' || !box.current) return;
    drag.current = { x: e.clientX, y: e.clientY, l: box.current.scrollLeft, t: box.current.scrollTop };
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onMove = (e: ReactPointerEvent) => {
    if (!drag.current || !box.current) return;
    box.current.scrollLeft = drag.current.l - (e.clientX - drag.current.x);
    box.current.scrollTop = drag.current.t - (e.clientY - drag.current.y);
  };

  return (
    <>
      <div
        ref={box}
        tabIndex={0}
        role="region"
        aria-label="Bild"
        onKeyDown={(e) => {
          if (e.key === '+' || e.key === '=') step(1.25);
          else if (e.key === '-') step(0.8);
          else if (e.key === '0') setZoom('fit');
          else if (e.key === '1') setZoom(1);
          else return;
          e.preventDefault();
        }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={() => (drag.current = null)}
        onPointerCancel={() => (drag.current = null)}
        className={cx('absolute inset-0 overflow-auto outline-none', zoom !== 'fit' && 'cursor-grab active:cursor-grabbing')}
      >
        <div className="flex min-h-full min-w-full items-center justify-center p-4">
          <img
            key={attempt}
            src={attempt ? `${src}${src.includes('?') ? '&' : '?'}r=${attempt}` : src}
            alt={it.filename}
            draggable={false}
            onLoad={(e) => {
              const img = e.currentTarget;
              // Maße des Originals, auch wenn gerade die Großansicht angezeigt wird (1:1 = Originalpixel)
              setNatural(it.width && it.height ? { w: it.width, h: it.height } : { w: img.naturalWidth, h: img.naturalHeight });
              setState('ok');
            }}
            onError={() => setState('error')}
            style={natural ? { width: natural.w * scale, height: natural.h * scale, maxWidth: 'none' } : { maxWidth: '100%', maxHeight: '100%' }}
            className={cx('object-contain select-none', state !== 'ok' && 'invisible')}
          />
        </div>
      </div>
      {state === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center text-[var(--ink-soft)]" role="status">
          <Loader2 className="animate-spin" aria-hidden /> <span className="sr-only">Bild wird geladen …</span>
        </div>
      )}
      {state === 'error' && (
        <div className="absolute inset-0">
          <NoPreview
            it={it}
            text="Das Bild konnte nicht geladen werden. Vielleicht wurde es gelöscht oder du hast keinen Zugriff mehr."
            onRetry={() => {
              setState('loading');
              setAttempt((a) => a + 1);
            }}
          />
        </div>
      )}
      {state === 'ok' && (
        <Toolbar>
          <ToolBtn label="Verkleinern (−)" onClick={() => step(0.8)}>
            <Minus size={17} />
          </ToolBtn>
          <span className="min-w-12 text-center text-xs tabular-nums" aria-live="polite">
            {Math.round(scale * 100)} %
          </span>
          <ToolBtn label="Vergrößern (+)" onClick={() => step(1.25)}>
            <Plus size={17} />
          </ToolBtn>
          <ToolBtn label="Originalgröße (1)" onClick={() => setZoom(1)}>
            1:1
          </ToolBtn>
          <ToolBtn label="An Fenster anpassen (0)" onClick={() => setZoom('fit')} disabled={zoom === 'fit'}>
            <Maximize size={16} />
          </ToolBtn>
        </Toolbar>
      )}
    </>
  );
}

/* ---------- PDF (pdf.js, lädt erst bei Bedarf) ---------- */

type PdfDoc = { numPages: number; getPage: (n: number) => Promise<PdfPage>; destroy: () => Promise<void> };
type PdfPage = {
  getViewport: (o: { scale: number }) => { width: number; height: number };
  render: (o: { canvas: HTMLCanvasElement; canvasContext: CanvasRenderingContext2D; viewport: unknown }) => { promise: Promise<void>; cancel: () => void };
};

/**
 * pdf.js bei Bedarf nachladen. Die „legacy“-Ausgabe bringt Polyfills für ältere mobile Browser mit
 * (die Standardausgabe nutzt u. a. `Map.getOrInsertComputed`, das viele Browser noch nicht kennen).
 */
async function loadPdfjs() {
  const [lib, worker] = await Promise.all([import('pdfjs-dist/legacy/build/pdf.mjs'), import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url')]);
  lib.GlobalWorkerOptions.workerSrc = worker.default;
  return lib;
}

function PdfView({ it, data, onRetry }: { it: PreviewItem; /** Bereits geladenes PDF (Office-Vorschau) */ data?: ArrayBuffer; onRetry?: () => void }) {
  const [doc, setDoc] = useState<PdfDoc | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rendering, setRendering] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    let loaded: PdfDoc | null = null;
    setError(false);
    setDoc(null);
    loadPdfjs()
      // pdf.js übergibt die Daten an den Worker (danach unbrauchbar) – darum immer eine Kopie
      .then((lib) => lib.getDocument(data ? { data: new Uint8Array(data.slice(0)) } : { url: it.url, withCredentials: true }).promise)
      .then((d) => {
        loaded = d as unknown as PdfDoc;
        if (alive) setDoc(loaded);
        else void loaded.destroy();
      })
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
      void loaded?.destroy();
    };
  }, [it.url, data, attempt]);

  useEffect(() => {
    if (!doc || !canvas.current || !box.current) return;
    let task: { promise: Promise<void>; cancel: () => void } | null = null;
    let alive = true;
    setRendering(true);
    void doc.getPage(page).then((p) => {
      if (!alive || !canvas.current || !box.current) return;
      const base = p.getViewport({ scale: 1 });
      const fit = Math.min(2, (box.current.clientWidth - 32) / base.width);
      const css = fit * zoom;
      const dpr = window.devicePixelRatio || 1;
      const vp = p.getViewport({ scale: css * dpr });
      const c = canvas.current;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      c.style.width = `${Math.floor(vp.width / dpr)}px`;
      c.style.height = `${Math.floor(vp.height / dpr)}px`;
      const ctx = c.getContext('2d');
      if (!ctx) return;
      task = p.render({ canvas: c, canvasContext: ctx, viewport: vp });
      task.promise.then(
        () => alive && setRendering(false),
        () => alive && setRendering(false),
      );
    });
    return () => {
      alive = false;
      task?.cancel();
    };
  }, [doc, page, zoom]);

  if (error)
    return (
      <NoPreview
        it={it}
        text={data ? 'Die Vorschau des Dokuments konnte nicht angezeigt werden.' : 'Die PDF-Datei konnte nicht angezeigt werden.'}
        onRetry={onRetry ?? (() => setAttempt((a) => a + 1))}
      />
    );
  return (
    <>
      <div
        ref={box}
        tabIndex={0}
        role="region"
        aria-label={doc ? `PDF, Seite ${page} von ${doc.numPages}` : 'PDF wird geladen'}
        onKeyDown={(e) => {
          if (!doc) return;
          if (e.key === 'PageDown' || e.key === 'ArrowDown' && e.altKey) setPage((p) => Math.min(doc.numPages, p + 1));
          else if (e.key === 'PageUp' || e.key === 'ArrowUp' && e.altKey) setPage((p) => Math.max(1, p - 1));
          else if (e.key === '+' || e.key === '=') setZoom((z) => Math.min(4, z * 1.25));
          else if (e.key === '-') setZoom((z) => Math.max(0.5, z / 1.25));
          else return;
          e.preventDefault();
        }}
        className="absolute inset-0 overflow-auto outline-none"
      >
        <div className="flex min-h-full min-w-full items-start justify-center p-4 pb-20">
          <canvas ref={canvas} className={cx('bg-white shadow', !doc && 'invisible')} aria-hidden />
        </div>
      </div>
      {(!doc || rendering) && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[var(--ink-soft)]" role="status">
          <Loader2 className="animate-spin" aria-hidden /> <span className="sr-only">PDF wird geladen …</span>
        </div>
      )}
      {doc && (
        <Toolbar>
          <ToolBtn label="Vorherige Seite (Bild ↑)" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            <ChevronLeft size={17} />
          </ToolBtn>
          <span className="min-w-20 text-center text-xs tabular-nums" aria-live="polite">
            Seite {page} von {doc.numPages}
          </span>
          <ToolBtn label="Nächste Seite (Bild ↓)" onClick={() => setPage((p) => Math.min(doc.numPages, p + 1))} disabled={page >= doc.numPages}>
            <ChevronRight size={17} />
          </ToolBtn>
          <span className="mx-1 h-5 w-px bg-[var(--line)]" aria-hidden />
          <ToolBtn label="Verkleinern (−)" onClick={() => setZoom((z) => Math.max(0.5, z / 1.25))}>
            <Minus size={17} />
          </ToolBtn>
          <ToolBtn label="Vergrößern (+)" onClick={() => setZoom((z) => Math.min(4, z * 1.25))}>
            <Plus size={17} />
          </ToolBtn>
        </Toolbar>
      )}
    </>
  );
}

/* ---------- Office (0.91: der Server wandelt mit LibreOffice in ein PDF um) ---------- */

function OfficeView({ it }: { it: PreviewItem }) {
  const [state, setState] = useState<{ pdf: ArrayBuffer } | { error: string; retry: boolean } | null>(null);
  const [attempt, setAttempt] = useState(0);
  const url = officePdfUrl(it);
  useEffect(() => {
    let alive = true;
    setState(null);
    fetch(attempt ? `${url}${url.includes('?') ? '&' : '?'}neu=1` : url, { credentials: 'same-origin' })
      .then(async (r) => {
        if (r.ok) return { pdf: await r.arrayBuffer() };
        const msg = await r
          .json()
          .then((j: { error?: string }) => j.error)
          .catch(() => undefined);
        // 501 = auf diesem Server nicht eingerichtet, 413 = zu groß: erneut versuchen hilft nicht
        return { error: msg || 'Die Vorschau konnte nicht erstellt werden.', retry: r.status !== 501 && r.status !== 413 };
      })
      .catch(() => ({ error: 'Die Vorschau konnte nicht geladen werden. Prüfe deine Verbindung.', retry: true }))
      .then((s) => alive && setState(s));
    return () => {
      alive = false;
    };
  }, [url, attempt]);
  if (!state)
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-[var(--ink-soft)]" role="status">
        <Loader2 className="animate-spin" aria-hidden />
        <p className="text-sm">Dokument wird für die Vorschau aufbereitet …</p>
        <p className="text-xs">Beim ersten Öffnen kann das einige Sekunden dauern.</p>
      </div>
    );
  if ('error' in state) return <NoPreview it={it} text={state.error} onRetry={state.retry ? () => setAttempt((a) => a + 1) : undefined} />;
  return <PdfView it={it} data={state.pdf} onRetry={() => setAttempt((a) => a + 1)} />;
}

/* ---------- Audio und Video (Bordmittel des Browsers, Server liefert Byte-Bereiche) ---------- */

function MediaPlayer({ it, kind }: { it: PreviewItem; kind: 'video' | 'audio' }) {
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  if (error)
    return (
      <NoPreview
        it={it}
        text={
          kind === 'video'
            ? 'Dieses Video kann dein Browser nicht abspielen (Format oder Codec wird nicht unterstützt). Lade es herunter, um es mit einer anderen App zu öffnen.'
            : 'Diese Audiodatei kann dein Browser nicht abspielen. Lade sie herunter, um sie mit einer anderen App zu öffnen.'
        }
        onRetry={() => {
          setError(false);
          setAttempt((a) => a + 1);
        }}
      />
    );
  if (kind === 'video')
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black p-0 sm:p-4">
        <video
          key={attempt}
          src={it.url}
          controls
          playsInline
          preload="metadata"
          aria-label={`Video: ${it.filename}`}
          onError={() => setError(true)}
          className="max-h-full max-w-full"
        />
      </div>
    );
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center">
      <span className="flex size-24 items-center justify-center rounded-3xl bg-accent-soft text-accent-fg">
        <FileIcon mime={it.mime} filename={it.typeName ?? it.filename} size={44} />
      </span>
      <p className="max-w-full truncate font-medium">{it.filename}</p>
      <audio key={attempt} src={it.url} controls preload="metadata" aria-label={`Audio: ${it.filename}`} onError={() => setError(true)} className="w-full max-w-md" />
    </div>
  );
}

/* ---------- Text (begrenzt, nur als Text) ---------- */

function TextView({ it }: { it: PreviewItem }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const tooBig = it.size > TEXT_LIMIT;
  useEffect(() => {
    if (tooBig) return;
    let alive = true;
    setError(false);
    fetch(it.url, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))))
      .then((t) => alive && setText(t))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [it.url, attempt, tooBig]);
  if (tooBig) return <NoPreview it={it} text="Die Textdatei ist zu groß für die Vorschau (über 512 KB)." />;
  if (error) return <NoPreview it={it} text="Die Datei konnte nicht geladen werden." onRetry={() => setAttempt((a) => a + 1)} />;
  if (text === null)
    return (
      <div className="flex h-full items-center justify-center text-[var(--ink-soft)]" role="status">
        <Loader2 className="animate-spin" aria-hidden />
      </div>
    );
  const delim = delimitedKind({ mime: it.mime, filename: it.typeName ?? it.filename });
  if (delim) return <TableText text={text} delimiter={delim === '\t' ? '\t' : detectDelimiter(text)} />;
  return (
    <div className="absolute inset-0 overflow-auto p-4" tabIndex={0} role="region" aria-label="Textinhalt">
      {/* React maskiert den Inhalt – er wird nie als HTML ausgeführt */}
      <pre className="mx-auto max-w-4xl rounded-xl border border-[var(--line)] bg-[var(--panel)] p-4 text-sm leading-relaxed break-words whitespace-pre-wrap">{text}</pre>
    </div>
  );
}

/* ---------- CSV/TSV als Tabelle (0.91) ---------- */

const TABLE_ROWS = 500;

function TableText({ text, delimiter }: { text: string; delimiter: string }) {
  const [raw, setRaw] = useState(false);
  const rows = useMemoRows(text, delimiter);
  const cols = Math.max(0, ...rows.slice(0, TABLE_ROWS).map((r) => r.length));
  return (
    <>
      <div className="absolute inset-0 overflow-auto p-4 pb-20" tabIndex={0} role="region" aria-label={raw ? 'Textinhalt' : 'Tabelle'}>
        {raw || cols < 2 ? (
          <pre className="mx-auto max-w-4xl rounded-xl border border-[var(--line)] bg-[var(--panel)] p-4 text-sm leading-relaxed break-words whitespace-pre-wrap">{text}</pre>
        ) : (
          <table className="border-collapse rounded-xl bg-[var(--panel)] text-sm">
            <thead>
              <tr>
                {Array.from({ length: cols }, (_, i) => (
                  <th key={i} scope="col" className="sticky top-0 border border-[var(--line)] bg-[var(--column)] px-2 py-1 text-left font-semibold whitespace-nowrap">
                    {rows[0]?.[i] ?? ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(1, TABLE_ROWS).map((r, ri) => (
                <tr key={ri}>
                  {Array.from({ length: cols }, (_, i) => (
                    <td key={i} className="max-w-[24rem] border border-[var(--line)] px-2 py-1 align-top break-words">
                      {r[i] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!raw && rows.length > TABLE_ROWS && <p className="mt-2 text-xs text-[var(--ink-soft)]">Die ersten {TABLE_ROWS - 1} von {rows.length - 1} Zeilen – die ganze Datei bekommst du über „Herunterladen“.</p>}
      </div>
      {cols >= 2 && (
        <Toolbar>
          <ToolBtn label={raw ? 'Als Tabelle zeigen' : 'Als Text zeigen'} onClick={() => setRaw((r) => !r)}>
            {raw ? 'Tabelle' : 'Text'}
          </ToolBtn>
        </Toolbar>
      )}
    </>
  );
}

function useMemoRows(text: string, delimiter: string) {
  return useMemo(() => parseCsv(text.replace(/^\uFEFF/, ''), delimiter).filter((r) => r.some((c) => c.trim() !== '')), [text, delimiter]);
}
